import React, { useState } from "react";
import PortoImage from "../assets/pp.jpg";
import { MdOutlineKeyboardArrowRight, MdCode, MdCloud, MdLocationOn, MdClose } from "react-icons/md";
import {Link} from "react-scroll";
import SocialLinks from "./SocialLinks";

const Home = () => {
  const [showMap, setShowMap] = useState(false);
  const allSkills = [
    "AI/ML",
    "Java",
    "Python",
    "Django",
    "React",
    "JavaScript",
    "TypeScript",
    "Node.js",
    "Spring Boot",
    "Golang",
    "AWS",
    "Azure",
    "GCP",
    "Docker",
    "Kubernetes",
    "Terraform",
    "Ansible",
    "Jenkins",
    "GitHub",
    "GitLab",
    "MongoDB",
    "MySQL",
    "PostgreSQL",
    "CI/CD",
  ];

  return (
    <div
      name="home"
      className="relative bg-gradient-to-b from-gray-950 via-slate-950 to-zinc-900 w-full py-6 sm:py-8 md:py-10 lg:py-12 pb-12 sm:pb-16 md:pb-20 lg:pb-24 text-white overflow-hidden"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-4 sm:left-10 w-48 h-48 sm:w-72 sm:h-72 bg-blue-500/15 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-4 sm:right-10 w-64 h-64 sm:w-96 sm:h-96 bg-purple-500/15 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative max-w-screen-lg mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-5rem)] px-4 sm:px-6 md:px-8 md:flex-row gap-2 sm:gap-4 md:gap-12 pt-20 md:pt-0">
        {/* Left Content */}
        <div className="flex flex-col justify-center z-10 flex-1 w-full md:w-auto text-center md:text-left">
          {/* Greeting */}
          <div className="mb-3 sm:mb-4 md:mb-5 flex items-center gap-2 sm:gap-3 justify-center md:justify-start">
            <span className="text-2xl sm:text-3xl md:text-4xl wave-animation">
              👋
            </span>
            <span className="text-blue-400 text-xs sm:text-sm md:text-base lg:text-lg font-medium tracking-wide uppercase">
              Hello, I'm
            </span>
          </div>
          
          {/* Name with Gradient */}
          <h1 className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold mb-3 sm:mb-4 md:mb-5 leading-[1.1] tracking-tight">
            <span className="bg-gradient-to-r from-white via-blue-200 to-cyan-300 bg-clip-text text-transparent drop-shadow-lg">
              Yemane Berhane
            </span>
          </h1>
          
          {/* Title with Icons */}
          <div className="flex items-center justify-center md:justify-start gap-2 sm:gap-3 mb-3 sm:mb-4 md:mb-5">
            <MdCode className="text-blue-400 hidden sm:block" size={24} />
            <MdCode className="text-blue-400 sm:hidden" size={20} />
            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-semibold text-gray-200 tracking-tight">
              Software & DevOps Engineer
            </h2>
            <MdCloud className="text-cyan-400 hidden sm:block" size={24} />
            <MdCloud className="text-cyan-400 sm:hidden" size={20} />
          </div>
          
          {/* Location */}
          <div className="mb-4 sm:mb-6 md:mb-8">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <MdLocationOn className="text-purple-400" size={18} />
              <span className="text-gray-300 text-sm sm:text-base md:text-lg font-medium">
                Columbus, Ohio
              </span>
            </div>
            {/* Map Widget */}
            <div className="relative group">
              <div 
                onClick={() => setShowMap(!showMap)}
                className="cursor-pointer w-full max-w-xs mx-auto md:mx-0 h-32 sm:h-40 rounded-lg overflow-hidden border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/20"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3055.5!2d-82.9988!3d39.9612!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883889c1b990de71%3A0x8e919ce7c4f5fbc!2sColumbus%2C%20OH!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(20%) brightness(0.8) contrast(1.2)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Columbus, Ohio Location"
                  className="pointer-events-none"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-xs font-medium">Click to view map</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Description */}
          <p className="text-gray-300 text-sm sm:text-base md:text-lg lg:text-xl mb-6 sm:mb-8 md:mb-10 max-w-lg mx-auto md:mx-0 leading-relaxed font-light tracking-wide">
            <span className="text-white font-semibold relative group">
              Senior Software Engineer
              <span className="absolute inset-0 bg-blue-500/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            </span>{" "}
            specializing in{" "}
            <span className="text-blue-400 font-semibold bg-blue-500/10 px-1.5 py-0.5 rounded hover:bg-blue-500/20 hover:text-blue-300 transition-all duration-300 cursor-default relative group">
              cloud-native microservices
              <span className="absolute inset-0 bg-blue-500/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span>,{" "}
            <span className="text-cyan-400 font-semibold bg-cyan-500/10 px-1.5 py-0.5 rounded hover:bg-cyan-500/20 hover:text-cyan-300 transition-all duration-300 cursor-default relative group">
              automated DevOps
              <span className="absolute inset-0 bg-cyan-500/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span>, and{" "}
            <span className="text-purple-400 font-semibold bg-purple-500/10 px-1.5 py-0.5 rounded hover:bg-purple-500/20 hover:text-purple-300 transition-all duration-300 cursor-default relative group">
              enterprise-scale applications
              <span className="absolute inset-0 bg-purple-500/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span> that drive{" "}
            <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent font-semibold hover:from-green-300 hover:to-emerald-300 transition-all duration-300 relative group">
              reliability
              <span className="absolute inset-0 bg-green-500/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span> and{" "}
            <span className="bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent font-semibold hover:from-orange-300 hover:to-yellow-300 transition-all duration-300 relative group">
              scalability
              <span className="absolute inset-0 bg-orange-500/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></span>
            </span>.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col gap-3 sm:gap-4 justify-center md:justify-start mb-0 sm:mb-0">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center md:justify-start">
              <Link 
                to="project" 
                smooth 
                duration={500} 
                className="group relative overflow-hidden px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg text-white text-sm sm:text-base font-semibold flex items-center justify-center gap-2 hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/50"
              >
                <span>View Projects</span>
                <MdOutlineKeyboardArrowRight 
                  size={20} 
                  className="sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300"
                />
              </Link>
              <Link 
                to="contact" 
                smooth 
                duration={500} 
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gray-900/50 backdrop-blur-sm border border-gray-700/40 rounded-lg text-white text-sm sm:text-base font-semibold hover:bg-gray-800/60 hover:border-blue-500/50 transition-all duration-300 hover:scale-105"
              >
                Get In Touch
              </Link>
            </div>
            {/* Social Links - Show on small screens only */}
            <div className="flex justify-center md:hidden mt-2">
              <SocialLinks inline={true} />
            </div>
          </div>
        </div>

        {/* Right Image */}
        <div className="relative z-10 flex-shrink-0 w-full sm:w-auto">
          {/* Glowing Border Effect */}
          <div className="relative group mx-auto max-w-[280px] sm:max-w-sm md:max-w-md transform transition-all duration-500 hover:scale-105">
            {/* Multiple Glow Layers for Depth */}
            <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 via-cyan-500 to-purple-600 rounded-2xl blur-xl opacity-40 group-hover:opacity-60 transition-opacity duration-300 animate-pulse"></div>
            <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-600 rounded-2xl blur opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
            
            {/* Image Container */}
            <div className="relative bg-gray-900/50 backdrop-blur-sm rounded-2xl p-2 border border-gray-700/40 transition-transform duration-500 shadow-2xl shadow-blue-500/20 group-hover:shadow-blue-500/40">
              <div className="overflow-hidden rounded-xl">
                <img
                  src={PortoImage}
                  alt="Yemane Berhane"
                  className="rounded-xl w-full object-cover transform scale-105 group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              
              {/* Focus Highlight Overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          </div>
          
          {/* Floating Badge with Enhanced Styling */}
          <div className="absolute -bottom-2 sm:-bottom-4 -right-2 sm:-right-4 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-xl shadow-blue-500/50 animate-bounce transform rotate-3 hover:rotate-0 transition-transform duration-300 z-20">
            <div className="flex items-center gap-1.5 sm:gap-2 text-white text-xs sm:text-sm font-semibold">
              <MdCloud size={16} className="sm:w-5 sm:h-5" />
              <span className="hidden xs:inline">6+ Years Experience</span>
              <span className="xs:hidden">6+ Yrs</span>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Section: Summary + Snapshot Experience + Key Skills */}
      <div className="relative max-w-screen-lg mx-auto px-4 sm:px-6 md:px-8 mt-4 sm:mt-6 md:mt-8">
        <div className="grid gap-4 md:gap-6 md:grid-cols-3">
          {/* Summary */}
          <div name="skills" className="md:col-span-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-2 tracking-wide">
              Quick Summary
            </h3>
            <ul className="space-y-1.5 text-[11px] sm:text-xs md:text-sm text-gray-300 leading-relaxed">
              <li>• 6+ years building cloud-native, production-grade systems.</li>
              <li>• Blend of backend, DevOps, and platform engineering.</li>
              <li>• Focus on reliability, scalability, and delivery speed.</li>
            </ul>
          </div>

          {/* Snapshot Experience */}
          <div className="md:col-span-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-2 tracking-wide">
              Recent Roles
            </h3>
            <div className="space-y-2">
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">
                  Software Engineer (AI/ML)
                </p>
                <p className="text-[11px] sm:text-xs text-gray-400">
                  JPMorgan Chase & Co. (JPMC) · 04-2026 – Present · Columbus, OH
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">
                  Sr. AWS DevOps / Developer
                </p>
                <p className="text-[11px] sm:text-xs text-gray-400">
                  NetJets · 02-2026 – 04-2026 · Columbus, OH
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">
                  Software / DevOps Engineer
                </p>
                <p className="text-[11px] sm:text-xs text-gray-400">
                  Upwork · 04-2024 – 06-2025 · Columbus, OH
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">
                  Software / DevOps Engineer
                </p>
                <p className="text-[11px] sm:text-xs text-gray-400">
                  JPMorgan Chase & Co. (JPMC) · 04-2024 – 06-2025 · Columbus, OH
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">
                  DevOps Engineer
                </p>
                <p className="text-[11px] sm:text-xs text-gray-400">
                  Wipro · 04-2024 – 07-2024 · Remote
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-300">
                  Sr. Software Engineer
                </p>
                <p className="text-[11px] sm:text-xs text-gray-400">
                  Olive AI · 03-2020 – 04-2023 · Columbus, OH
                </p>
              </div>
            </div>
          </div>

          {/* Key Skills */}
          <div className="md:col-span-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
            <h3 className="text-sm sm:text-base font-semibold text-white mb-2 tracking-wide">
              Key Skills
            </h3>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {allSkills.map((skill) => (
                <span
                  key={skill}
                  className="px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full bg-gradient-to-r from-blue-600/20 to-cyan-500/20 border border-blue-500/40 text-blue-200"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Full Screen Map Modal */}
      {showMap && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setShowMap(false)}
        >
          <div 
            className="relative w-full max-w-4xl h-[80vh] rounded-xl overflow-hidden border-2 border-purple-500/50 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowMap(false)}
              className="absolute top-4 right-4 z-10 bg-gray-900/90 hover:bg-gray-800 rounded-full p-2 text-white transition-all duration-300 hover:scale-110 shadow-lg"
              aria-label="Close map"
            >
              <MdClose size={24} />
            </button>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3055.5!2d-82.9988!3d39.9612!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883889c1b990de71%3A0x8e919ce7c4f5fbc!2sColumbus%2C%20OH!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Columbus, Ohio - Full Map View"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;